/**
 * What the publication summary draws: a catalog work's details and its
 * reader-facing summary, with nothing about how it reached the screen. A
 * message share and a reply draft's source both carry these fields, so each
 * hands its own record over as is and the two cannot drift into two layouts.
 */
export type PublicationView = {
  kind: 'article' | 'trial' | 'nccn_guideline' | 'web';
  title: string | null;
  authors: string[];
  citation: string;
  journal: string | null;
  journalAbbr: string | null;
  year: number | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  doi: string | null;
  pmid: string | null;
  pmcid: string | null;
  nctId: string | null;
  summary: PublicationSummaryView | null;
};

export type PublicationSummaryView = {
  whyItMatters: string | null;
  headlineResult: string | null;
  design: string | null;
  population: string | null;
  intervention: string | null;
  safety: string | null;
  authorsConclusion: string | null;
  limitations: string | null;
};

/**
 * Journal, year, volume, issue and pages in AMA's order, for example
 * "N Engl J Med. 2020;382(9):810-821". Whatever the catalog lacks is left out
 * rather than shown as a gap.
 */
export function formatPublicationSource(publication: PublicationView): string | undefined {
  const journal = publication.journalAbbr ?? publication.journal;

  const volumeIssue = publication.volume
    ? `${publication.volume}${publication.issue ? `(${publication.issue})` : ''}`
    : '';

  const pages = publication.pages ? `:${publication.pages}` : '';

  const yearPart = publication.year
    ? `${publication.year}${volumeIssue ? `;${volumeIssue}` : ''}${pages}`
    : '';

  const parts = [journal, yearPart].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join('. ') : undefined;
}

export interface PublicationIdentifier {
  label: string;
  value: string;
}

export function getPublicationIdentifiers(publication: PublicationView): PublicationIdentifier[] {
  const identifiers: [string, string | null][] = [
    ['DOI', publication.doi],
    ['PMID', publication.pmid],
    ['PMCID', publication.pmcid],
    ['ClinicalTrials.gov', publication.nctId],
  ];

  return identifiers.flatMap(([label, value]) => (value ? [{ label, value }] : []));
}

export function getOpenPublicationLabel(kind: PublicationView['kind'] | undefined): string {
  switch (kind) {
    case 'article':
      return 'Open article';
    case 'trial':
      return 'Open trial';
    case 'nccn_guideline':
      return 'Open guideline';
    default:
      return 'Open link';
  }
}
